import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { sessionNoteService } from '../services/SessionNoteService';
import { sessionService } from '../services/SessionService';
import { travelService } from '../services/TravelService';
import { UNIFIED_SESSIONS } from '../utils/featureFlags';
import { appointmentService } from '../services/AppointmentService';
import { userService } from '../services/UserService';
import { clientService } from '../services/ClientService';
import { locationVerificationService } from '../services/LocationVerificationService';
import { locationAuditService } from '../services/LocationAuditService';
import { getCurrentPosition } from '../utils/geolocation';
import { LocationCheckIn } from '../components/LocationCheckIn';
import { PreSessionChecklist } from '../components/PreSessionChecklist';
import { PhotoVerification } from '../components/PhotoVerification';
import { SessionPreparation } from '../components/SessionPreparation';
import { SessionPlanReview } from '../components/SessionPlanReview';
import { EmergencyInfo } from '../components/EmergencyInfo';
import { CameraStream } from '../components/CameraStream';
import { VoiceNoteButton } from '../components/VoiceNoteButton';
import { useVoiceCommands } from '../hooks/useVoiceCommands';
import { storageService } from '../services/StorageService';
import { Appointment, AppointmentWithNames } from '../models/Appointment';
import { SessionNote, QuickNote, ProgramData, BehaviorData, PromptLevel } from '../models/SessionNote';
import { Client } from '../models/Client';
import { Timestamp } from 'firebase/firestore';
import { FileText, Video, Clock, Send, Plus, Trash2, ChevronUp, ChevronDown, EyeOff, Play } from 'lucide-react';

// Play one of the pre-recorded VERA confirmation clips (best-effort).
const playVeraAudio = (file: string) => {
  try {
    const audio = new Audio(`${process.env.PUBLIC_URL || ''}/audio/vera/${file}`);
    audio.play().catch(() => {
      /* autoplay/permission — non-fatal */
    });
  } catch {
    /* noop */
  }
};

type SessionStage = 'selection' | 'session-plan' | 'preparation' | 'checklist' | 'photo-verify' | 'check-in' | 'in-session' | 'post-session';

const SessionNotes: React.FC = () => {
  const { user } = useAuth();
  const { company } = useCompany();
  const navigate = useNavigate();

  // Stage management
  const [stage, setStage] = useState<SessionStage>('selection');

  // Appointment selection
  const [todaysAppointments, setTodaysAppointments] = useState<AppointmentWithNames[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentWithNames | null>(null);
  const [sessionNote, setSessionNote] = useState<SessionNote | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Check-in
  const [gettingLocation, setGettingLocation] = useState(false);
  // Values below are write-only for now (setters drive check-in flow state); getters not yet rendered.
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locationWarning, setLocationWarning] = useState<string | null>(null);
  const [requiresLocationApproval, setRequiresLocationApproval] = useState(false);
  /* eslint-enable @typescript-eslint/no-unused-vars */

  // During session
  const [sessionStarted, setSessionStarted] = useState(false);
  const [streamActive, setStreamActive] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [streamSessionId, setStreamSessionId] = useState<string | null>(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [quickNoteInput, setQuickNoteInput] = useState('');
  const [quickNotes, setQuickNotes] = useState<QuickNote[]>([]);

  // Post-session draft
  const [sessionSummary, setSessionSummary] = useState('');
  const [programData, setProgramData] = useState<ProgramData[]>([]);
  const [behaviorData, setBehaviorData] = useState<BehaviorData[]>([]);
  const [concerns, setConcerns] = useState<string[]>(['']);
  const [parentCommunication, setParentCommunication] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Success/error messages
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // UI state for collapsible header during session
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [privacyMode, setPrivacyMode] = useState(false);

  // Wake word + commands run only while the session is actively in progress.
  const isSessionActive = sessionStarted && stage === 'in-session';

  // Privacy Mode — shared by the "Hey VERA, privacy" voice command and the
  // on-screen button. Mutes the outgoing supervisor stream (via the paused prop
  // on CameraStream) and blocks dictation (disabled prop on VoiceNoteButton),
  // shows the banner, and logs the pause window for the compliance trail.
  const enterPrivacyMode = () => {
    if (privacyMode) return;
    setPrivacyMode(true);
    playVeraAudio('vera-privacy-mode.mp3');
    if (sessionNote && user) {
      sessionNoteService
        .logPrivacyPauseStart(sessionNote.id, user.id)
        .catch((e) => console.error('Privacy pause log failed:', e));
    }
  };

  const exitPrivacyMode = () => {
    if (!privacyMode) return;
    setPrivacyMode(false);
    playVeraAudio('vera-resuming.mp3');
    if (sessionNote) {
      sessionNoteService
        .logPrivacyPauseEnd(sessionNote.id)
        .catch((e) => console.error('Privacy resume log failed:', e));
    }
  };

  // "Hey VERA" wake word — each detection toggles Privacy Mode (pause ↔ resume).
  const voice = useVoiceCommands({
    isSessionActive,
    onWakeWord: () => (privacyMode ? exitPrivacyMode() : enterPrivacyMode()),
  });

  // Load today's appointments for the RBT
  useEffect(() => {
    if (user && user.role === 'rbt') {
      loadTodaysAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Session timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (sessionStarted && stage === 'in-session') {
      interval = setInterval(() => {
        setSessionDuration(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [sessionStarted, stage]);

  const loadTodaysAppointments = async () => {
    if (!user) return;

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const appointments = await appointmentService.getAppointmentsByRbt(user.id);

      // Filter for today's appointments
      const todaysAppts = appointments.filter((appt: Appointment) => {
        const apptDate = new Date(appt.startTime);
        apptDate.setHours(0, 0, 0, 0);
        return apptDate.getTime() === today.getTime();
      });

      // Enrich appointments with names
      const enrichedAppts: AppointmentWithNames[] = await Promise.all(
        todaysAppts.map(async (appt) => {
          const [client, supervisor] = await Promise.all([
            clientService.getClient(appt.clientId),
            userService.getUser(appt.supervisorId),
          ]);

          return {
            ...appt,
            clientName: client ? `${client.firstName} ${client.lastName}` : 'Unknown Client',
            rbtName: user.name,
            supervisorName: supervisor?.name || 'Unknown Supervisor',
          };
        })
      );

      setTodaysAppointments(enrichedAppts);
    } catch (err) {
      console.error('Error loading appointments:', err);
      setErrorMessage('Failed to load today\'s appointments');
    }
  };

  const handleSelectAppointment = async (appointment: AppointmentWithNames) => {
    if (!user) return;

    setSelectedAppointment(appointment);

    // Load client data for emergency info
    const client = await clientService.getClient(appointment.clientId);
    setSelectedClient(client);

    // Check if session note already exists
    let existingNote = await sessionNoteService.getSessionNoteByAppointment(appointment.id, user.id);

    if (!existingNote) {
      // Create new session note
      const noteId = await sessionNoteService.createSessionNote(
        {
          appointmentId: appointment.id,
          sessionId: `session-${appointment.id}`,
          rbtId: user.id,
          rbtName: user.name,
          rbtCredentials: user.credentials,
          supervisorId: appointment.supervisorId,
          supervisorName: appointment.supervisorName,
          clientId: appointment.clientId,
          companyId: user.companyId,
        },
        user.id
      );

      existingNote = await sessionNoteService.getSessionNote(noteId);
    }

    setSessionNote(existingNote);
    setStage('session-plan');
  };

  const handleContinueFromSessionPlan = () => {
    setStage('preparation');
  };

  const handleContinueFromPreparation = () => {
    setStage('checklist');
  };

  const handleChecklistComplete = async (checkedItems: Record<string, boolean>) => {
    if (!user || !sessionNote) return;

    try {
      await sessionNoteService.savePreSessionChecklist(sessionNote.id, checkedItems, user.id);
      setSuccessMessage('Checklist completed!');
      setStage('photo-verify');
    } catch (err) {
      console.error('Checklist save error:', err);
      setErrorMessage('Failed to save checklist');
    }
  };

  const handlePhotoCapture = async (photoDataUrl: string, location: { latitude: number; longitude: number }) => {
    if (!user || !sessionNote || !selectedAppointment) return;

    try {
      // Upload photo to Firebase Storage using client-focused structure
      const blob = await fetch(photoDataUrl).then((res) => res.blob());
      const sessionDate = selectedAppointment.startTime instanceof Date
        ? selectedAppointment.startTime
        : (selectedAppointment.startTime as any).toDate();
      const photoUrl = await storageService.uploadVerificationPhoto(
        blob,
        selectedAppointment.clientId,
        sessionNote.id,
        sessionDate
      );

      // Save photo verification data
      await sessionNoteService.savePhotoVerification(sessionNote.id, photoUrl, location, user.id);

      setSuccessMessage('Photo verified! Please check in with your location.');
      setStage('check-in');
    } catch (err) {
      console.error('Photo upload error:', err);
      throw new Error('Failed to upload verification photo');
    }
  };

  const handleCheckIn = async (location?: { latitude: number; longitude: number; accuracy: number }) => {
    if (!user || !sessionNote || !selectedAppointment) return;

    setGettingLocation(true);
    setLocationError(null);
    setLocationWarning(null);

    try {
      // Use location from LocationCheckIn component, or get it if not provided
      let checkInLocation: { latitude: number; longitude: number; accuracy: number };

      if (location) {
        checkInLocation = location;
      } else {
        // Fallback: Get GPS location directly (native: Capacitor; web: navigator)
        const position = await getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });

        checkInLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
      }

      // Verify location if appointment has GPS coordinates
      if (selectedAppointment.location.latitude && selectedAppointment.location.longitude) {
        const verification = locationVerificationService.verifyLocation(
          checkInLocation.latitude,
          checkInLocation.longitude,
          selectedAppointment.location.latitude,
          selectedAppointment.location.longitude,
          `${selectedAppointment.location.address}, ${selectedAppointment.location.city}`
        );

        if (!verification.isVerified) {
          setLocationWarning(verification.warningMessage || 'Location verification failed');
          setRequiresLocationApproval(true);

          // Still record check-in but mark as requiring approval
          await sessionNoteService.recordCheckIn(
            sessionNote.id,
            checkInLocation,
            user.id,
            {
              isVerified: false,
              distance: verification.distance,
              requiresApproval: true,
              status: 'flagged',
              appointmentAddress: `${selectedAppointment.location.address}, ${selectedAppointment.location.city}, ${selectedAppointment.location.state}`,
              appointmentLatitude: selectedAppointment.location.latitude,
              appointmentLongitude: selectedAppointment.location.longitude,
            }
          );

          // Create audit log entry for flagged location
          await locationAuditService.createAuditLog({
            companyId: user.companyId,
            sessionNoteId: sessionNote.id,
            appointmentId: selectedAppointment.id,
            rbtId: user.id,
            rbtName: user.name,
            checkInLatitude: checkInLocation.latitude,
            checkInLongitude: checkInLocation.longitude,
            appointmentLatitude: selectedAppointment.location.latitude!,
            appointmentLongitude: selectedAppointment.location.longitude!,
            appointmentAddress: `${selectedAppointment.location.address}, ${selectedAppointment.location.city}, ${selectedAppointment.location.state}`,
            distance: verification.distance,
            isVerified: false,
            requiresApproval: true,
            status: 'flagged',
          });

          setSuccessMessage('Checked in - Location flagged for supervisor review');
        } else {
          // Location verified successfully
          await sessionNoteService.recordCheckIn(
            sessionNote.id,
            checkInLocation,
            user.id,
            {
              isVerified: true,
              distance: verification.distance,
              requiresApproval: false,
              status: 'verified',
              appointmentAddress: `${selectedAppointment.location.address}, ${selectedAppointment.location.city}, ${selectedAppointment.location.state}`,
              appointmentLatitude: selectedAppointment.location.latitude,
              appointmentLongitude: selectedAppointment.location.longitude,
            }
          );

          // Create audit log entry for verified location
          await locationAuditService.createAuditLog({
            companyId: user.companyId,
            sessionNoteId: sessionNote.id,
            appointmentId: selectedAppointment.id,
            rbtId: user.id,
            rbtName: user.name,
            checkInLatitude: checkInLocation.latitude,
            checkInLongitude: checkInLocation.longitude,
            appointmentLatitude: selectedAppointment.location.latitude!,
            appointmentLongitude: selectedAppointment.location.longitude!,
            appointmentAddress: `${selectedAppointment.location.address}, ${selectedAppointment.location.city}, ${selectedAppointment.location.state}`,
            distance: verification.distance,
            isVerified: true,
            requiresApproval: false,
            status: 'verified',
          });

          setSuccessMessage(`Checked in successfully! (${locationVerificationService.formatDistance(verification.distance)} from appointment)`);
        }
      } else {
        // No appointment GPS coordinates - can't verify against a reference, but
        // still record the check-in in the location audit trail as 'unverified'.
        await sessionNoteService.recordCheckIn(sessionNote.id, checkInLocation, user.id);
        await locationAuditService.createAuditLog({
          companyId: user.companyId,
          sessionNoteId: sessionNote.id,
          appointmentId: selectedAppointment.id,
          rbtId: user.id,
          rbtName: user.name,
          checkInLatitude: checkInLocation.latitude,
          checkInLongitude: checkInLocation.longitude,
          appointmentLatitude: 0,
          appointmentLongitude: 0,
          appointmentAddress: `${selectedAppointment.location.address}, ${selectedAppointment.location.city}, ${selectedAppointment.location.state}`,
          distance: 0,
          isVerified: false,
          requiresApproval: false,
          status: 'unverified',
        });
        setSuccessMessage('Checked in successfully!');
      }

      // Fold "I'm here" arrival into this existing check-in: if the RBT tapped
      // "On my way" for this appointment, close that trip using the SAME GPS fix
      // as arrival — one action confirms arrival AND starts the session, rather
      // than making the RBT tap twice for the same moment. No active trip for this
      // appointment => no auto mileage for the leg (forfeit); manual claims are a
      // separate path. Mileage capture must never block clinical check-in.
      try {
        const activeTrip = await travelService.getActiveTrip(user.id);
        if (activeTrip && activeTrip.appointmentId === selectedAppointment.id) {
          const expectedLatitude =
            selectedAppointment.location.latitude ?? selectedClient?.latitude ?? undefined;
          const expectedLongitude =
            selectedAppointment.location.longitude ?? selectedClient?.longitude ?? undefined;
          const completed = await travelService.completeTrip(
            activeTrip.id,
            {
              latitude: checkInLocation.latitude,
              longitude: checkInLocation.longitude,
              accuracy: checkInLocation.accuracy,
              timestamp: Timestamp.now(),
            },
            {
              expectedLatitude,
              expectedLongitude,
              mileageRates: company?.settings?.mileageRates,
            }
          );
          // Give the RBT explicit confirmation that arrival (and mileage) captured.
          const miles = (completed.distance ?? 0).toFixed(2);
          const note =
            completed.classification === 'reimbursable'
              ? `Arrival recorded — ${miles} mi travel captured.`
              : `Arrival recorded — ${miles} mi (commute leg, not reimbursed).`;
          setSuccessMessage((m) => (m ? `${m} ${note}` : note));
        }
      } catch (tripErr) {
        console.error('Travel trip completion failed (non-blocking):', tripErr);
      }

      setStage('in-session');
    } catch (err: any) {
      console.error('Location error:', err);
      setLocationError(err.message || 'Failed to get location. Please enable location services.');
    } finally {
      setGettingLocation(false);
    }
  };

  // --- Phase 2 mirror (flag-gated) -----------------------------------------
  // Keep the canonical companies/sessions record in sync with this session's
  // lifecycle so sessions started from ANY entry point appear in My Sessions +
  // BCBA review and close their appointment. Best-effort: never breaks the
  // legacy sessionNotes flow. No-op unless UNIFIED_SESSIONS is on.
  const mirrorSessionIdRef = useRef<string | null>(null);
  const coerceDate = (d: any): Date =>
    d instanceof Date ? d : d?.toDate ? d.toDate() : new Date(d);

  const mirrorStart = async () => {
    if (!UNIFIED_SESSIONS || !user || !selectedAppointment) return;
    try {
      const appt = selectedAppointment;
      const clientAddress = [appt.location?.address, appt.location?.city, appt.location?.state]
        .filter(Boolean)
        .join(', ');
      mirrorSessionIdRef.current = await sessionService.findOrCreateByAppointment({
        companyId: user.companyId,
        appointmentId: appt.id,
        clientId: appt.clientId,
        clientName: appt.clientName,
        rbtId: user.id,
        rbtName: user.name,
        supervisorId: appt.supervisorId || '',
        supervisorName: appt.supervisorName || '',
        scheduledStart: coerceDate(appt.startTime),
        scheduledEnd: coerceDate(appt.endTime),
        clientAddress,
        sessionType: appt.supervisionRequired ? 'supervision' : 'direct-therapy',
      });
    } catch (e) {
      console.error('[mirror] start failed:', e);
    }
  };

  const mirrorEnd = async () => {
    if (!UNIFIED_SESSIONS || !user || !mirrorSessionIdRef.current) return;
    try {
      await sessionService.endSession(user.companyId, mirrorSessionIdRef.current);
    } catch (e) {
      console.error('[mirror] end failed:', e);
    }
  };

  const mirrorSubmit = async () => {
    if (!UNIFIED_SESSIONS || !user || !mirrorSessionIdRef.current) return;
    try {
      await sessionService.submitNotes(
        user.companyId,
        mirrorSessionIdRef.current,
        {
          sessionSummary,
          quickNotes,
          programData,
          behaviorData,
          concerns: concerns.filter((c) => c.trim() !== ''),
          parentCommunication: parentCommunication || '',
        },
        'rbt-session'
      );
    } catch (e) {
      console.error('[mirror] submit failed:', e);
    }
  };
  // -------------------------------------------------------------------------

  const handleStartSession = async () => {
    if (!user || !sessionNote) return;

    await sessionNoteService.startSession(sessionNote.id, user.id);
    setSessionStarted(true);
    setSuccessMessage('Session started!');
    void mirrorStart();
  };

  const handleStreamStart = async (streamId: string) => {
    if (!user || !sessionNote) return;

    setStreamSessionId(streamId);
    setStreamActive(true);
    await sessionNoteService.startStream(sessionNote.id, user.id);
    setSuccessMessage('Live stream started...');
  };

  const handleStreamEnd = async () => {
    if (!user || !sessionNote) return;

    setStreamActive(false);
    setStreamSessionId(null);
    await sessionNoteService.endStream(sessionNote.id, user.id);
  };

  const handleStreamError = (error: string) => {
    setErrorMessage(error);
    setStreamActive(false);
  };

  const handleAddQuickNote = (entryMethod: 'text' | 'voice' = 'text', overrideText?: string) => {
    const text = (overrideText ?? quickNoteInput).trim();
    if (!text) return;

    const note: QuickNote = {
      timestamp: Timestamp.now(),
      content: text,
      entryMethod,
    };

    setQuickNotes((prev) => [...prev, note]);
    setQuickNoteInput('');
  };

  const handleEndSession = async () => {
    if (!user || !sessionNote) return;

    await sessionNoteService.endSession(sessionNote.id, user.id);
    await mirrorEnd();

    if (streamActive) {
      await sessionNoteService.endStream(sessionNote.id, user.id);
      setStreamActive(false);
    }

    setSessionStarted(false);
    setStage('post-session');
    setSuccessMessage('Session ended. Please complete the session summary.');
  };

  const handleAddProgramData = () => {
    setProgramData([
      ...programData,
      {
        programId: `prog-${Date.now()}`,
        programName: '',
        trialsCompleted: 0,
        correctResponses: 0,
        incorrectResponses: 0,
        promptLevel: 'independent',
        notes: '',
      },
    ]);
  };

  const handleUpdateProgramData = (index: number, field: keyof ProgramData, value: any) => {
    const updated = [...programData];
    updated[index] = { ...updated[index], [field]: value };
    setProgramData(updated);
  };

  const handleRemoveProgramData = (index: number) => {
    setProgramData(programData.filter((_, i) => i !== index));
  };

  const handleAddBehaviorData = () => {
    setBehaviorData([
      ...behaviorData,
      {
        behaviorId: `beh-${Date.now()}`,
        behaviorName: '',
        frequency: 0,
        antecedents: '',
        consequences: '',
        notes: '',
      },
    ]);
  };

  const handleUpdateBehaviorData = (index: number, field: keyof BehaviorData, value: any) => {
    const updated = [...behaviorData];
    updated[index] = { ...updated[index], [field]: value };
    setBehaviorData(updated);
  };

  const handleRemoveBehaviorData = (index: number) => {
    setBehaviorData(behaviorData.filter((_, i) => i !== index));
  };

  const handleSubmitDraft = async () => {
    console.log('handleSubmitDraft called', { user: !!user, sessionNote: !!sessionNote, sessionSummary });

    if (!user) {
      setErrorMessage('User not found. Please refresh and try again.');
      return;
    }

    if (!sessionNote) {
      setErrorMessage('Session note not found. Please refresh and try again.');
      return;
    }

    if (!sessionSummary.trim()) {
      setErrorMessage('Please enter a session summary before submitting.');
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      console.log('Submitting draft for session note:', sessionNote.id);
      await sessionNoteService.submitRBTDraft(
        sessionNote.id,
        {
          quickNotes,
          sessionSummary,
          programData,
          behaviorData,
          concerns: concerns.filter(c => c.trim() !== ''),
          parentCommunication: parentCommunication || undefined,
        },
        user.id
      );
      await mirrorSubmit();

      console.log('Draft submitted successfully');
      setSuccessMessage('Draft submitted successfully! Your supervisor will review it.');

      // Reset form
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err: any) {
      console.error('Error submitting draft:', err);
      setErrorMessage(`Failed to submit draft: ${err.message || 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!user || user.role !== 'rbt') {
    return (
      <div style={{ padding: 'var(--spacing-8)', textAlign: 'center' }}>
        <p style={{ color: 'var(--color-gray-600)' }}>
          This page is only accessible to RBTs.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--spacing-6)' }}>
      {/* Privacy Mode banner — visual confirmation alongside the audio cue */}
      {privacyMode && (
        <div
          role="status"
          aria-live="assertive"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 300,
            backgroundColor: '#7a1020',
            color: '#ffffff',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <EyeOff size={18} />
            Privacy Mode — recording and supervisor stream are paused
          </span>
          <button
            type="button"
            onClick={exitPrivacyMode}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', backgroundColor: '#ffffff', color: '#7a1020', border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' }}
          >
            <Play size={14} />
            Resume
          </button>
        </div>
      )}

      <div style={{ marginBottom: 'var(--spacing-6)' }}>
        <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'bold', marginBottom: 'var(--spacing-2)' }}>
          <FileText style={{ display: 'inline', marginRight: 'var(--spacing-2)' }} />
          Session Notes
        </h1>
        <p style={{ color: 'var(--color-gray-600)' }}>
          Document your therapy session
        </p>
      </div>

      {successMessage && (
        <div style={{
          backgroundColor: 'var(--color-success-50)',
          border: '1px solid var(--color-success-200)',
          color: 'var(--color-success-800)',
          padding: 'var(--spacing-4)',
          borderRadius: 'var(--radius-lg)',
          marginBottom: 'var(--spacing-4)',
        }}>
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div style={{
          backgroundColor: 'var(--color-error-50)',
          border: '1px solid var(--color-error-200)',
          color: 'var(--color-error-800)',
          padding: 'var(--spacing-4)',
          borderRadius: 'var(--radius-lg)',
          marginBottom: 'var(--spacing-4)',
        }}>
          {errorMessage}
        </div>
      )}

      {/* STAGE: Appointment Selection */}
      {stage === 'selection' && (
        <div>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'bold', marginBottom: 'var(--spacing-4)' }}>
            Select Today's Appointment
          </h2>

          {todaysAppointments.length === 0 ? (
            <p style={{ color: 'var(--color-gray-600)' }}>
              No appointments scheduled for today.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
              {todaysAppointments.map(appt => (
                <div
                  key={appt.id}
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--spacing-4)',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleSelectAppointment(appt)}
                >
                  <div style={{ marginBottom: 'var(--spacing-2)' }}>
                    <strong>{appt.clientName}</strong>
                  </div>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
                    {new Date(appt.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {' - '}
                    {new Date(appt.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginTop: 'var(--spacing-1)' }}>
                    {appt.location.address}, {appt.location.city}, {appt.location.state}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* STAGE: Session Plan Review */}
      {stage === 'session-plan' && selectedAppointment && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-gray-300 rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Session Plan</h2>
            <div className="text-lg font-semibold text-gray-900">{selectedAppointment.clientName}</div>
            <div className="text-sm text-gray-600 mt-1">
              {new Date(selectedAppointment.startTime).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })} - {selectedAppointment.location.address}
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded-lg p-6">
            <SessionPlanReview
              clientName={selectedAppointment.clientName}
              sessionPlan={selectedAppointment.sessionPlan}
              onContinue={handleContinueFromSessionPlan}
            />
          </div>
        </div>
      )}

      {/* STAGE: Session Preparation */}
      {stage === 'preparation' && selectedAppointment && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-gray-300 rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Previous Sessions</h2>
            <div className="text-lg font-semibold text-gray-900">{selectedAppointment.clientName}</div>
            <div className="text-sm text-gray-600 mt-1">
              {new Date(selectedAppointment.startTime).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })} - {selectedAppointment.location.address}
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded-lg p-6">
            <SessionPreparation
              clientId={selectedAppointment.clientId}
              clientName={selectedAppointment.clientName}
              onContinue={handleContinueFromPreparation}
            />
          </div>
        </div>
      )}

      {/* STAGE: Pre-Session Checklist */}
      {stage === 'checklist' && selectedAppointment && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-gray-300 rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Session Preparation</h2>
            <div className="text-lg font-semibold text-gray-900">{selectedAppointment.clientName}</div>
            <div className="text-sm text-gray-600 mt-1">
              {new Date(selectedAppointment.startTime).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })} - {selectedAppointment.location.address}
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded-lg p-6">
            <PreSessionChecklist
              clientName={selectedAppointment.clientName}
              onComplete={handleChecklistComplete}
            />
          </div>

          {errorMessage && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              {errorMessage}
            </div>
          )}
        </div>
      )}

      {/* STAGE: Photo Verification */}
      {stage === 'photo-verify' && selectedAppointment && user && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-gray-300 rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Identity Verification</h2>
            <div className="text-lg font-semibold text-gray-900">{selectedAppointment.clientName}</div>
            <div className="text-sm text-gray-600 mt-1">
              Session with {selectedAppointment.rbtName}
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded-lg p-6">
            <PhotoVerification
              rbtName={user.name}
              onPhotoCapture={handlePhotoCapture}
            />
          </div>

          {successMessage && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
              {successMessage}
            </div>
          )}

          {errorMessage && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              {errorMessage}
            </div>
          )}
        </div>
      )}

      {/* STAGE: Check-In */}
      {stage === 'check-in' && selectedAppointment && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-gray-300 rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Check In</h2>
            <div className="text-lg font-semibold text-gray-900">{selectedAppointment.clientName}</div>
            <div className="text-sm text-gray-600 mt-1">
              Session with {selectedAppointment.rbtName}
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded-lg p-6">
            <LocationCheckIn
              onCheckIn={handleCheckIn}
              appointmentAddress={`${selectedAppointment.location.address}, ${selectedAppointment.location.city}, ${selectedAppointment.location.state}`}
              disabled={gettingLocation}
            />
          </div>

          {successMessage && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
              {successMessage}
            </div>
          )}
        </div>
      )}

      {/* STAGE: In-Session */}
      {stage === 'in-session' && (
        <div>
          {/* Collapsible Header */}
          <div style={{
            backgroundColor: '#1e40af',
            color: 'white',
            borderRadius: '8px',
            marginBottom: '16px',
            overflow: 'hidden',
          }}>
            <button
              onClick={() => setHeaderCollapsed(!headerCollapsed)}
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={20} />
                <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Session Notes</span>
              </div>
              {headerCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
            </button>
            {!headerCollapsed && (
              <div style={{ padding: '0 16px 12px 16px', fontSize: '14px', opacity: 0.9 }}>
                Document your therapy session
              </div>
            )}
          </div>

          {/* Success Message */}
          {successMessage && (
            <div style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              color: '#166534',
              fontWeight: 500,
            }}>
              {successMessage}
            </div>
          )}

          {/* Session Controls */}
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px',
          }}>
            {/* Client Name and Timer */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{selectedAppointment?.clientName}</div>
              <div style={{ fontSize: '14px', color: '#6b6b6b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Clock size={14} />
                {formatDuration(sessionDuration)}
              </div>
            </div>

            {/* Session Control Buttons - Always show a button */}
            {!sessionStarted ? (
              <button
                onClick={handleStartSession}
                style={{
                  backgroundColor: '#16a34a',
                  color: 'white',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '18px',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <Video size={24} />
                Start Session
              </button>
            ) : (
              <button
                onClick={handleEndSession}
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  width: '100%',
                }}
              >
                End Session
              </button>
            )}
          </div>

          {/* Camera Stream - Only show after session started */}
          {sessionStarted && sessionNote && selectedAppointment && user && (
            <div style={{
              marginBottom: '16px',
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              overflow: 'hidden',
            }}>
              <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid #d1d5db',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Video size={18} color="#4F6367" />
                  <span style={{ fontWeight: 'bold' }}>Live Stream</span>
                </div>
                {streamActive && (
                  <span style={{
                    backgroundColor: '#dc2626',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}>
                    LIVE
                  </span>
                )}
              </div>
              <CameraStream
                sessionNoteId={sessionNote.id}
                rbtId={user.id}
                rbtName={user.name}
                supervisorId={selectedAppointment.supervisorId}
                onStreamStart={handleStreamStart}
                onStreamEnd={handleStreamEnd}
                onError={handleStreamError}
                paused={privacyMode}
              />
            </div>
          )}

          {/* Emergency Contact Information */}
          {selectedClient && (
            <div style={{ marginBottom: '16px' }}>
              <EmergencyInfo client={selectedClient} />
            </div>
          )}

          {/* Quick Notes */}
          {sessionStarted && (
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              padding: '16px',
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>
                Quick Notes
              </h3>

              {/* Privacy Mode control + voice command status */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {!privacyMode ? (
                  <button
                    type="button"
                    onClick={enterPrivacyMode}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: 'transparent', color: '#7a1020', border: '1px solid #7a1020', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    <EyeOff size={16} /> Privacy Pause
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={exitPrivacyMode}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    <Play size={16} /> Resume
                  </button>
                )}
                <span style={{ fontSize: '12px', color: '#6b6b6b' }}>
                  {voice.status === 'loading' && 'Starting “Hey VERA”…'}
                  {voice.status === 'listening' && 'Voice on — say “Hey VERA” to pause or resume.'}
                  {(voice.status === 'denied' || voice.status === 'error') &&
                    `${voice.error || 'Voice commands unavailable.'} Use the Privacy Pause button.`}
                </span>
              </div>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={quickNoteInput}
                  onChange={(e) => setQuickNoteInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddQuickNote('text')}
                  placeholder="Add a quick note (or tap Voice to dictate)..."
                  style={{
                    flex: '1 1 200px',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '16px',
                  }}
                />
                <VoiceNoteButton
                  onInterim={(text) => setQuickNoteInput(text)}
                  onFinal={(text) => handleAddQuickNote('voice', text)}
                  disabled={privacyMode}
                  keyterms={selectedAppointment?.clientName ? [selectedAppointment.clientName] : undefined}
                />
                <button
                  onClick={() => handleAddQuickNote('text')}
                  style={{
                    backgroundColor: '#4F6367',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Add
                </button>
              </div>

              {/* Display quick notes */}
              {quickNotes.length > 0 && (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {quickNotes.map((note, index) => (
                    <div
                      key={index}
                      style={{
                        backgroundColor: '#f9fafb',
                        padding: '12px',
                        borderRadius: '6px',
                        fontSize: '14px',
                      }}
                    >
                      <div style={{ color: '#6b6b6b', fontSize: '12px', marginBottom: '4px' }}>
                        {note.timestamp.toDate().toLocaleTimeString()}
                      </div>
                      <div>{note.content}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* STAGE: Post-Session */}
      {stage === 'post-session' && (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '800px',
          margin: '0 auto',
        }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>
            Session Summary
          </h2>

          {/* Session Summary */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
              Overall Session Summary *
            </label>
            <textarea
              value={sessionSummary}
              onChange={(e) => setSessionSummary(e.target.value)}
              placeholder="Brief summary of today's session..."
              rows={4}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '16px',
                resize: 'vertical',
              }}
              required
            />
          </div>

          {/* Program Data */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <label style={{ fontWeight: 'bold' }}>Program Data</label>
              <button
                onClick={handleAddProgramData}
                style={{
                  backgroundColor: '#4F6367',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '14px',
                }}
              >
                <Plus size={16} />
                Add Program
              </button>
            </div>

            {programData.map((program, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '16px',
                  marginBottom: '12px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <input
                    type="text"
                    value={program.programName}
                    onChange={(e) => handleUpdateProgramData(index, 'programName', e.target.value)}
                    placeholder="Program name"
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      marginRight: '8px',
                    }}
                  />
                  <button
                    onClick={() => handleRemoveProgramData(index)}
                    style={{
                      backgroundColor: '#dc2626',
                      color: 'white',
                      padding: '8px',
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b6b6b' }}>Trials</label>
                    <input
                      type="number"
                      value={program.trialsCompleted}
                      onChange={(e) => handleUpdateProgramData(index, 'trialsCompleted', parseInt(e.target.value) || 0)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b6b6b' }}>Correct</label>
                    <input
                      type="number"
                      value={program.correctResponses}
                      onChange={(e) => handleUpdateProgramData(index, 'correctResponses', parseInt(e.target.value) || 0)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b6b6b' }}>Prompt Level</label>
                    <select
                      value={program.promptLevel}
                      onChange={(e) => handleUpdateProgramData(index, 'promptLevel', e.target.value as PromptLevel)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                      }}
                    >
                      <option value="independent">Independent</option>
                      <option value="verbal">Verbal</option>
                      <option value="gestural">Gestural</option>
                      <option value="physical">Physical</option>
                      <option value="full">Full</option>
                    </select>
                  </div>
                </div>

                <textarea
                  value={program.notes}
                  onChange={(e) => handleUpdateProgramData(index, 'notes', e.target.value)}
                  placeholder="Program notes..."
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    resize: 'vertical',
                  }}
                />
              </div>
            ))}
          </div>

          {/* Behavior Data */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <label style={{ fontWeight: 'bold' }}>Behavior Data</label>
              <button
                onClick={handleAddBehaviorData}
                style={{
                  backgroundColor: '#4F6367',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '14px',
                }}
              >
                <Plus size={16} />
                Add Behavior
              </button>
            </div>

            {behaviorData.map((behavior, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '16px',
                  marginBottom: '12px',
                }}
              >
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <input
                    type="text"
                    value={behavior.behaviorName}
                    onChange={(e) => handleUpdateBehaviorData(index, 'behaviorName', e.target.value)}
                    placeholder="Behavior name"
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                    }}
                  />
                  <input
                    type="number"
                    value={behavior.frequency}
                    onChange={(e) => handleUpdateBehaviorData(index, 'frequency', parseInt(e.target.value) || 0)}
                    placeholder="Frequency"
                    style={{
                      width: '100px',
                      padding: '8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                    }}
                  />
                  <button
                    onClick={() => handleRemoveBehaviorData(index)}
                    style={{
                      backgroundColor: '#dc2626',
                      color: 'white',
                      padding: '8px',
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input
                    type="text"
                    value={behavior.antecedents}
                    onChange={(e) => handleUpdateBehaviorData(index, 'antecedents', e.target.value)}
                    placeholder="Antecedents"
                    style={{
                      padding: '8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}
                  />
                  <input
                    type="text"
                    value={behavior.consequences}
                    onChange={(e) => handleUpdateBehaviorData(index, 'consequences', e.target.value)}
                    placeholder="Consequences"
                    style={{
                      padding: '8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Concerns */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
              Concerns or Incidents
            </label>
            {concerns.map((concern, index) => (
              <input
                key={index}
                type="text"
                value={concern}
                onChange={(e) => {
                  const updated = [...concerns];
                  updated[index] = e.target.value;
                  setConcerns(updated);
                }}
                placeholder="Any concerns or incidents to report?"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  marginBottom: '8px',
                }}
              />
            ))}
          </div>

          {/* Parent Communication */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
              Parent/Caregiver Communication
            </label>
            <textarea
              value={parentCommunication}
              onChange={(e) => setParentCommunication(e.target.value)}
              placeholder="Any communication with parent or caregiver..."
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '16px',
                resize: 'vertical',
              }}
            />
          </div>

          {/* Submit Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button
              onClick={handleSubmitDraft}
              disabled={submitting || !sessionSummary.trim()}
              style={{
                backgroundColor: submitting || !sessionSummary.trim() ? '#9ca3af' : '#16a34a',
                color: 'white',
                padding: '12px 32px',
                borderRadius: '6px',
                border: 'none',
                cursor: submitting || !sessionSummary.trim() ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Send size={18} />
              {submitting ? 'Submitting...' : 'Submit for Review'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionNotes;
